/**
 * Persistent Storage Service
 * Requests persistent storage quota from the browser to ensure game data
 * isn't automatically cleared when device storage is low.
 *
 * Critical for iOS PWA offline functionality and preventing data loss.
 */

export interface StorageQuotaInfo {
  persisted: boolean;
  quota: number;
  usage: number;
  percentUsed: number;
}

/**
 * Request persistent storage from the browser.
 *
 * On iOS: Allows the browser to keep IndexedDB data permanently
 * On other browsers: Requests persistent quota instead of temporary quota
 *
 * @returns Promise<boolean> - true if persistent storage was granted
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!navigator.storage || !navigator.storage.persist) {
      console.warn('🔒 Storage API not available - persistent storage unavailable');
      return false;
    }

    const isPersisted = await navigator.storage.persist();

    if (isPersisted) {
      console.log('✅ Persistent storage granted - game data will be kept safe');
    } else {
      console.warn('⚠️ Persistent storage denied - game data may be cleared if device storage is low');
    }

    return isPersisted;
  } catch (error) {
    console.warn('🔒 Could not request persistent storage:', error);
    return false;
  }
}

/**
 * Get current storage quota and usage information.
 * Useful for monitoring storage usage on the device.
 *
 * @returns Promise<StorageQuotaInfo | null> - Storage quota info or null if unavailable
 */
export async function getStorageQuotaInfo(): Promise<StorageQuotaInfo | null> {
  try {
    if (!navigator.storage || !navigator.storage.estimate) {
      return null;
    }

    const estimate = await navigator.storage.estimate();
    const quota = estimate.quota || 0;
    const usage = estimate.usage || 0;
    const isPersisted = await navigator.storage.persisted?.();

    return {
      persisted: isPersisted ?? false,
      quota,
      usage,
      percentUsed: quota > 0 ? Math.round((usage / quota) * 100) : 0,
    };
  } catch (error) {
    console.warn('Could not retrieve storage quota info:', error);
    return null;
  }
}

/**
 * Check if persistent storage is currently granted.
 *
 * @returns Promise<boolean> - true if persistent storage is active
 */
export async function isPersistentStorageGranted(): Promise<boolean> {
  try {
    if (!navigator.storage || !navigator.storage.persisted) {
      return false;
    }
    return navigator.storage.persisted();
  } catch {
    return false;
  }
}
